import { normalizeJourneyStage, validateJourneyStageExplanation } from './journeyStageExplanation.js';

export const JOURNEY_BATCH8_EXPLANATIONS = Object.freeze({
  'imaging-diagnostics': Object.freeze({
    before: Object.freeze({
      linkIds: Object.freeze(['prepare', 'where', 'access']),
      questionIndexes: Object.freeze([1, 2, 4, 6]),
      roleIds: Object.freeze([
        'radiographer',
        'phlebotomist',
        'healthcare_assistant',
        'receptionist',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Before your test, check the appointment instructions carefully. You may need to avoid food or drink, change the timing of some medicines, arrive early or have a blood test first. Tell the team about allergies, pregnancy or implants. Also tell them about any mobility, communication or other support needs.',
        whyItMayHappen: 'Different tests need different preparation so that the investigation is safe and gives useful information. The team may need to check that the test requested is still appropriate and whether any extra precautions or support are needed.',
        whatTeamIsDoing: 'The diagnostic team is checking your identity, the investigation requested, relevant safety information and any preparation that should happen before the test. They may also arrange reasonable adjustments or clarify instructions with the referring clinical team.',
        variationNote: 'There is no single preparation rule for all diagnostic tests. For example, some scans need contrast, some require no special preparation, and some blood or physiological tests have their own instructions. Follow the information for your specific appointment rather than general advice.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'attend-test',
            title: 'Attend for the planned test',
            stageId: 'visit',
            when: 'When the preparation instructions are clear and the investigation can go ahead',
          }),
          Object.freeze({
            id: 'clarify-preparation',
            title: 'Clarify preparation or safety information first',
            stageId: 'before',
            description: 'The team may need to check medicines, allergies, pregnancy, implants, kidney function or another issue before the investigation.',
            when: 'If the appointment instructions are unclear or something has changed since the test was requested',
          }),
          Object.freeze({
            id: 'rearrange-test',
            title: 'Rearrange or change the test if needed',
            stageId: 'before',
            when: 'If the investigation cannot safely or usefully go ahead as planned that day',
          }),
        ]),
      }),
    }),
    visit: Object.freeze({
      linkIds: Object.freeze(['where', 'access']),
      questionIndexes: Object.freeze([0, 3]),
      roleIds: Object.freeze([
        'radiographer',
        'sonographer',
        'phlebotomist',
        'cardiac_physiologist',
        'healthcare_science_practitioner',
        'healthcare_assistant',
      ]),
      explanation: Object.freeze({
        whatHappens: 'During the visit, the exact process depends on the test. You may have a blood sample, an X-ray, CT, MRI or ultrasound, or another diagnostic or physiological test. Some investigations use a cannula, contrast or monitoring. Staff should explain what they need you to do and how long the test is likely to take.',
        whyItMayHappen: 'The investigation is collecting information that can help the clinical team understand symptoms, monitor a known condition, plan treatment or check how treatment is working.',
        whatTeamIsDoing: 'The person performing the test is checking that the correct investigation is carried out safely and that the images, measurements or samples are good enough to be reviewed. Depending on the test, another professional may later interpret or report the result.',
        variationNote: 'The person carrying out a test may not be the person who makes the final diagnosis or discusses the result with you. Some findings can be discussed on the day, but many tests need formal review first. Ask what to expect for your specific investigation.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'finish-and-leave',
            title: 'Finish the test and leave the department',
            stageId: 'results',
            when: 'If the investigation is complete and no observation or immediate treatment is needed afterwards',
          }),
          Object.freeze({
            id: 'short-observation',
            title: 'Stay briefly for observation or another check',
            stageId: 'visit',
            when: 'If the test, contrast, medicine or your clinical situation means the team wants to monitor you for a little longer',
          }),
          Object.freeze({
            id: 'additional-test',
            title: 'Have an additional or different investigation',
            stageId: 'visit',
            when: 'If the team identifies that another test is needed to answer the clinical question or complete the planned assessment',
          }),
        ]),
      }),
    }),
    results: Object.freeze({
      linkIds: Object.freeze(['results']),
      questionIndexes: Object.freeze([5]),
      roleIds: Object.freeze([
        'radiologist',
        'biomedical_scientist',
        'healthcare_science_practitioner',
        'consultant',
      ]),
      explanation: Object.freeze({
        whatHappens: 'After the test, images, measurements or samples may need to be checked and formally reported. The result is usually sent to the clinician or team that requested the investigation. They can explain what the result means alongside your other clinical information.',
        whyItMayHappen: 'A diagnostic result often needs specialist interpretation and may need to be compared with previous tests, symptoms or other findings before the clinical team can decide what it means.',
        whatTeamIsDoing: 'Diagnostic professionals are reviewing the quality and findings of the test and sending the result through the appropriate reporting route. The referring team then decides whether you need reassurance, another test, treatment, follow-up or an earlier review.',
        variationNote: 'Result times vary widely between tests and services. An individual result does not always provide a diagnosis by itself. If you have not heard within the timeframe you were given, use the contact route provided by the service rather than assuming the result is normal.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'routine-result',
            title: 'Receive the result through the planned route',
            when: 'If the result can be reviewed and discussed at the expected appointment or by the usual service',
          }),
          Object.freeze({
            id: 'more-tests',
            title: 'Have further tests or specialist review',
            when: 'If the result does not fully answer the clinical question or shows something that needs more assessment',
          }),
          Object.freeze({
            id: 'treatment-or-follow-up',
            title: 'Move to treatment, monitoring or follow-up',
            when: 'If the result gives the clinical team enough information to agree the next step with you',
          }),
        ]),
      }),
    }),
  }),

  radiotherapy: Object.freeze({
    planning: Object.freeze({
      linkIds: Object.freeze(['planning']),
      questionIndexes: Object.freeze([0, 1, 2, 3]),
      roleIds: Object.freeze([
        'consultant',
        'therapeutic_radiographer',
        'clinical_nurse_specialist',
        'radiographer',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Before radiotherapy starts, the oncology team will explain why treatment is recommended, the area being treated and the expected benefits and side effects. You will usually have a planning appointment. This may include a CT scan and careful positioning. Some people need a fitted mask, mould or another support to help them stay in the same position for treatment.',
        whyItMayHappen: 'Radiotherapy needs to be targeted accurately. Planning helps the team decide the treatment area, dose and schedule while limiting radiation to nearby healthy tissues as much as possible.',
        whatTeamIsDoing: 'The clinical oncology and radiotherapy teams are combining your scans and clinical information to create an individual treatment plan. They check the position you will use, explain preparation instructions and confirm that the plan is ready before treatment begins.',
        variationNote: 'Planning and preparation depend on the part of the body being treated and the aim of treatment. Not everyone needs a mask, contrast, special diet or the same number of visits. Follow the instructions for your own treatment plan.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'start-radiotherapy',
            title: 'Start the planned radiotherapy course',
            stageId: 'treatment',
            when: 'When planning is complete and the treatment plan has been checked and approved',
          }),
          Object.freeze({
            id: 'more-planning',
            title: 'Have additional planning or checks',
            stageId: 'planning',
            when: 'If the team needs another scan, a different position or more information before treatment can safely begin',
          }),
          Object.freeze({
            id: 'change-treatment-plan',
            title: 'Review or change the treatment plan',
            stageId: 'planning',
            when: 'If your health, scans or treatment priorities change before radiotherapy starts',
          }),
        ]),
      }),
    }),
    treatment: Object.freeze({
      linkIds: Object.freeze(['treatment', 'effects']),
      questionIndexes: Object.freeze([4, 5]),
      roleIds: Object.freeze([
        'therapeutic_radiographer',
        'consultant',
        'clinical_nurse_specialist',
        'healthcare_assistant',
        'dietitian',
      ]),
      explanation: Object.freeze({
        whatHappens: 'At each treatment visit, therapeutic radiographers help you into the planned position and use the radiotherapy machine to deliver treatment to the target area. The treatment itself is usually brief, although positioning and checks can take longer. You can normally communicate with the radiographers while the machine is being used.',
        whyItMayHappen: 'Radiotherapy is often delivered over a series of visits so that the planned total treatment can be given safely. The number and spacing of visits depend on the type of cancer, the aim of treatment and the plan agreed for you.',
        whatTeamIsDoing: 'The radiographers are checking your identity, position and treatment details at every visit. The wider team monitors side effects and may adjust supportive medicines, skin care, nutrition advice or other support if needed.',
        variationNote: 'Side effects depend on the area treated and can build gradually during a course of radiotherapy. A missed or delayed appointment does not always mean the whole course has failed, but contact the radiotherapy team promptly so they can advise what should happen next.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'continue-course',
            title: 'Continue the planned treatment course',
            stageId: 'treatment',
            when: 'If treatment is going ahead as planned and side effects remain manageable',
          }),
          Object.freeze({
            id: 'support-review',
            title: 'Have an extra review for side effects or support',
            stageId: 'treatment',
            when: 'If symptoms, nutrition, skin changes, pain, fatigue or another issue needs more help during treatment',
          }),
          Object.freeze({
            id: 'complete-course',
            title: 'Complete radiotherapy and move to follow-up',
            stageId: 'after',
            when: 'When the planned course has finished or the team has agreed that treatment should stop',
          }),
        ]),
      }),
    }),
    after: Object.freeze({
      linkIds: Object.freeze(['effects', 'follow-up']),
      questionIndexes: Object.freeze([6]),
      roleIds: Object.freeze([
        'consultant',
        'therapeutic_radiographer',
        'clinical_nurse_specialist',
        'dietitian',
      ]),
      explanation: Object.freeze({
        whatHappens: 'After the final radiotherapy visit, side effects may take time to settle and some can temporarily become more noticeable before improving. You should be given advice about recovery, symptoms to report, medicines or skin care if relevant, and what follow-up has been arranged.',
        whyItMayHappen: 'Radiotherapy continues to have biological effects after the last treatment visit, so recovery and assessment of treatment response do not always happen immediately.',
        whatTeamIsDoing: 'The team is helping you manage short-term effects, watching for problems that need earlier review and planning how the effect of treatment will be assessed. Follow-up may involve clinic review, scans or shared care with another cancer team.',
        variationNote: 'The timing of recovery and follow-up depends on the area treated, the treatment aim and any other cancer treatment you are having. Use the specific urgent-contact advice from your radiotherapy team if new or worsening symptoms occur.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'recover-and-monitor',
            title: 'Recover and monitor symptoms',
            stageId: 'after',
            when: 'If treatment is complete and side effects can be managed with the plan you have been given',
          }),
          Object.freeze({
            id: 'planned-follow-up',
            title: 'Attend planned oncology follow-up',
            stageId: 'after',
            when: 'When the team needs to review recovery, treatment effects or the next stage of cancer care',
          }),
          Object.freeze({
            id: 'earlier-review',
            title: 'Contact the treatment team earlier if needed',
            stageId: 'after',
            when: 'If symptoms become more severe, a new problem develops or the team has told you a change needs prompt assessment',
          }),
        ]),
      }),
    }),
  }),

  chemotherapy: Object.freeze({
    planning: Object.freeze({
      linkIds: Object.freeze(['planning']),
      questionIndexes: Object.freeze([0, 1]),
      roleIds: Object.freeze([
        'consultant',
        'clinical_nurse_specialist',
        'pharmacist',
        'staff_nurse',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Before chemotherapy or another systemic anti-cancer treatment starts, the team explains which medicines are being recommended, what the treatment is aiming to do, how it will be given and the main side effects and urgent symptoms to know about. Blood tests and other checks are often needed before treatment.',
        whyItMayHappen: 'Systemic anti-cancer treatments affect the body in different ways, so the team needs to make sure the planned treatment and dose are suitable and that you understand the expected benefits, risks and alternatives.',
        whatTeamIsDoing: 'The oncology team is reviewing your diagnosis, treatment goals, blood results, other health conditions and medicines. They also plan how the treatment will be given and whether you may need a cannula, PICC line, port or another route.',
        variationNote: 'Not all systemic anti-cancer treatment is chemotherapy. Treatment can include different kinds of anti-cancer medicines, and the schedule, blood-test requirements and side effects vary between treatments. Follow the information for your own regimen rather than another patient’s plan.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'start-treatment',
            title: 'Start the agreed treatment',
            stageId: 'treatment',
            when: 'When the required checks are satisfactory and you are ready to proceed with the agreed treatment plan',
          }),
          Object.freeze({
            id: 'repeat-checks',
            title: 'Repeat blood tests or other checks first',
            stageId: 'planning',
            when: 'If the team needs more information before deciding whether treatment should go ahead or the dose should change',
          }),
          Object.freeze({
            id: 'review-plan',
            title: 'Review or change the treatment plan',
            stageId: 'planning',
            when: 'If your health, test results, preferences or treatment goals mean the original plan may no longer be the best option',
          }),
        ]),
      }),
    }),
    treatment: Object.freeze({
      linkIds: Object.freeze(['treatment', 'medicines']),
      questionIndexes: Object.freeze([2, 3]),
      roleIds: Object.freeze([
        'clinical_nurse_specialist',
        'staff_nurse',
        'pharmacist',
        'consultant',
        'healthcare_assistant',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Treatment may be given in a cancer day unit, clinic or sometimes at home, depending on the medicine. It may be given through a cannula, PICC line or port, as tablets, or by another route. Staff usually check how you have been since the last treatment and may review blood results before giving the next dose.',
        whyItMayHappen: 'Treatment is often organised in cycles or repeated visits so the team can balance the anti-cancer effect with time for your body to recover. The schedule depends on the medicines and the aim of treatment.',
        whatTeamIsDoing: 'Nurses, pharmacists and the oncology team are checking the prescription, dose, blood results, treatment route and any side effects. They may give medicines such as anti-sickness treatment and monitor for immediate reactions where relevant.',
        variationNote: 'Your treatment may look very different from someone else’s, even if both are described as chemotherapy or SACT. Some treatments take minutes and others take several hours. The team may delay, reduce or change a dose if that is safer for you.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'continue-cycle',
            title: 'Continue the planned treatment cycle',
            stageId: 'treatment',
            when: 'If your checks are satisfactory and treatment is continuing as planned',
          }),
          Object.freeze({
            id: 'delay-or-adjust',
            title: 'Delay or adjust treatment',
            stageId: 'treatment',
            when: 'If blood results, side effects, infection or another health issue means the next dose should be changed or postponed',
          }),
          Object.freeze({
            id: 'between-treatment-support',
            title: 'Move to recovery and support between treatments',
            stageId: 'support',
            when: 'After the treatment visit, while you recover and monitor for side effects before the next planned review or dose',
          }),
        ]),
      }),
    }),
    support: Object.freeze({
      linkIds: Object.freeze(['medicines', 'urgent']),
      questionIndexes: Object.freeze([4, 5, 6]),
      roleIds: Object.freeze([
        'clinical_nurse_specialist',
        'staff_nurse',
        'pharmacist',
        'consultant',
        'dietitian',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Between treatments, you may need supportive medicines and advice about side effects, eating and drinking, activity and infection risk. Your team should give you a 24-hour or urgent contact route and explain which symptoms must be reported straight away rather than waiting for the next appointment.',
        whyItMayHappen: 'Some side effects appear after you leave the treatment unit and blood counts can change between cycles. Early advice can help the team treat problems promptly and decide whether the next treatment needs to change.',
        whatTeamIsDoing: 'The cancer team is monitoring symptoms, blood results and how well you are tolerating treatment. They may arrange extra tests, supportive care, treatment for side effects or an earlier clinical review before deciding on the next cycle.',
        variationNote: 'Urgent-contact advice differs between treatment regimens and services. Follow the temperature threshold and symptom advice given by your own cancer team. If you have been told a symptom needs urgent contact, do not wait for a routine appointment.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'next-treatment',
            title: 'Return for the next planned treatment',
            stageId: 'treatment',
            when: 'If you have recovered sufficiently and the pre-treatment checks allow the next cycle or dose to go ahead',
          }),
          Object.freeze({
            id: 'supportive-review',
            title: 'Have additional supportive care or review',
            stageId: 'support',
            when: 'If side effects, blood results, nutrition, pain or another issue needs treatment or more support',
          }),
          Object.freeze({
            id: 'treatment-review',
            title: 'Review the wider treatment plan',
            stageId: 'support',
            when: 'When the oncology team needs to assess response, side effects or whether treatment should continue, change or finish',
          }),
        ]),
      }),
    }),
  }),
});

function patientSummary(explanation) {
  return [
    explanation?.whatHappens,
    explanation?.whyItMayHappen,
    explanation?.whatTeamIsDoing,
    explanation?.variationNote,
  ].filter(Boolean).join(' ');
}

export function applyJourneyBatch8Explanation(templateId, stages = []) {
  const stageMap = JOURNEY_BATCH8_EXPLANATIONS[templateId];
  if (!stageMap) return stages;
  const knownStageIds = stages.map((stage) => stage.id);
  return stages.map((stage) => {
    const enrichment = stageMap[stage.id];
    if (!enrichment) return stage;
    const enriched = normalizeJourneyStage({
      ...stage,
      ...enrichment,
      description: patientSummary(enrichment.explanation),
    });
    const errors = validateJourneyStageExplanation(enriched, knownStageIds);
    if (errors.length) throw new Error(`${templateId} stage ${stage.id}: ${errors.join('; ')}`);
    return enriched;
  });
}
